/**
 * Computes all derived boolean flags for return policy display logic.
 *
 * @param {object} item - Product item containing return/exchange flags
 * @param {boolean} isGSTSelected - Whether GST invoice is selected
 * @param {object} [options]
 * @param {boolean} [options.includeFraud=true] - Pass false for order confirm (no fraud blocking)
 */
export function computeReturnPolicyFlags(item, isGSTSelected, { includeFraud = true } = {}) {
  const {
    returnWindow = 0,
    isReturnable,
    isExchangeable,
    isCatalogFraudReturnable,
    isCatalogFraudExchangeable
  } = item;

  const fraudBlocksReturn =
    includeFraud && !isReturnable && isCatalogFraudReturnable && returnWindow > 0;
  const fraudBlocksExchange =
    includeFraud && !isExchangeable && isCatalogFraudExchangeable && returnWindow > 0;
  const gstBlocksExchange = isGSTSelected && isExchangeable && returnWindow > 0;
  const exchangeBlocked = gstBlocksExchange || fraudBlocksExchange;
  const hasStrikeout = fraudBlocksReturn || exchangeBlocked;

  return {
    returnWindow,
    isReturnable,
    isExchangeable,
    isCatalogFraudReturnable,
    isCatalogFraudExchangeable,
    fraudBlocksReturn,
    fraudBlocksExchange,
    gstBlocksExchange,
    exchangeBlocked,
    hasStrikeout
  };
}

/**
 * Builds all display message strings for return policy with day/days substitution applied.
 *
 * @param {object} revampConfig - Config from getEnableNonReturnableProductRevampConfig selector
 * @param {number} returnWindow - Return window in days
 * @param {boolean} isMobile - Whether to use msite or desktop label keys
 */
export function computeReturnMessages(revampConfig, returnWindow, isMobile) {
  const {
    daysReturnMsite = '',
    daysReturnDesktop = '',
    daysExchangeMsite = '',
    daysExchangeDesktop = '',
    returnAndExchangeAvailableMsite = '',
    returnAndExchangeAvailableDesktop = '',
    noReturnMsite = '',
    noReturnDesktop = '',
    noExchangeMsite = 'No Exchange',
    noExchangeDesktop = 'No Exchange',
    noReturnAndExchangeMsite = '',
    noReturnAndExchangeDesktop = ''
  } = revampConfig || {};

  const dayDaysWrapper = (msg = '') => (returnWindow > 1 ? msg.replace('day', 'days') : msg);

  const returnRaw = isMobile ? daysReturnMsite : daysReturnDesktop;
  const exchangeRaw = isMobile ? daysExchangeMsite : daysExchangeDesktop;
  const returnAndExchangeRaw = isMobile
    ? returnAndExchangeAvailableMsite
    : returnAndExchangeAvailableDesktop;

  return {
    returnMsg: dayDaysWrapper(returnRaw.replace('%x', returnWindow)),
    exchangeMsg: dayDaysWrapper(exchangeRaw.replace('%x', returnWindow)),
    returnAndExchangeMsg: dayDaysWrapper(returnAndExchangeRaw.replace('%x', returnWindow)),
    messageNoReturnAndExchangeDesktop: isMobile
      ? noReturnAndExchangeMsite
      : noReturnAndExchangeDesktop,
    messageNonReturnableDesktop: isMobile ? noReturnMsite : noReturnDesktop,
    messageNoExchangeDesktop: isMobile ? noExchangeMsite : noExchangeDesktop
  };
}

/**
 * Returns icon paths for return policy icons.
 *
 * @param {string} imageBaseUrl - Base image URL (without /static/img/)
 */
export function getReturnPolicyIcons(imageBaseUrl) {
  return {
    nonReturnableTickSvg: `${imageBaseUrl}/static/img/grey-tick.svg`,
    nonReturnableCloseSvg: `${imageBaseUrl}/static/img/nonreturnable-close.svg`
  };
}
